import React from 'react';
import { createChatStore, type ChatStore, type Id, type Message } from '@norkin/chat-core';
import { ChatProvider, Conversation, useThread } from '@norkin/chat-react';
import { runOpenAIChatWithTools, type ToolDefinition, type ToolHandler } from '@norkin/chat-openai';
import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ChatMessageList } from './ChatMessageList';
import { ChatComposer } from './ChatComposer';
import { toolHandlers as defaultToolHandlers } from './tool-handlers';

export type ShadcnChatProps = {
  apiBase: string; // Full chat completions endpoint, e.g., http://localhost:8787/api/chat/completions
  threadId?: Id;
  model?: string; // Model identifier to pass to the OpenAI-compatible endpoint (defaults to gpt-4.1)
  systemPrompt?: string; // Optional system prompt; pass an empty string to omit or leave undefined for the default helper prompt
  tools?: ToolDefinition[];
  toolHandlers?: Record<string, ToolHandler>;
  handleToolsLocally?: boolean; // When false, skip local tool execution and rely on remote runtime tool.result.* SSE frames (defaults to true)
};

const DEFAULT_MODEL = 'gpt-4.1';
const DEFAULT_SYSTEM = 'You are a helpful assistant.';
const DEFAULT_TOOLS: ToolDefinition[] = [
  {
    type: 'function',
    function: {
      name: 'get_weather',
      description: 'Demo tool handler',
      parameters: {
        type: 'object',
        properties: {
          location: { type: 'string', description: 'City and state, e.g. San Francisco, CA' },
        },
      },
    },
  },
];

type ConversationChildProps = {
  messages: Message[];
  containerProps: {
    ref: React.RefObject<HTMLDivElement>;
    onScroll?: React.UIEventHandler<HTMLDivElement>;
  };
  bottomAnchorProps: {
    ref: React.RefObject<HTMLDivElement>;
  };
  atBottom: boolean;
  hasScrollableContent: boolean;
  scrollToBottom: () => void;
  usesWindowScroll: boolean;
};

type ConversationViewportProps = ConversationChildProps & {
  threadId: Id;
  isStreaming: boolean;
};

function ConversationViewport({
  threadId,
  isStreaming,
  messages,
  containerProps,
  bottomAnchorProps,
  atBottom,
  hasScrollableContent,
  scrollToBottom,
  usesWindowScroll,
}: ConversationViewportProps) {
  const anchorRef = bottomAnchorProps.ref;
  const containerRef = containerProps.ref;
  const [anchorVisible, setAnchorVisible] = React.useState(true);

  React.useEffect(() => {
    if (!hasScrollableContent) {
      setAnchorVisible(true);
      return;
    }

    const anchor = anchorRef?.current;
    const container = containerRef?.current;

    if (!anchor) {
      setAnchorVisible(atBottom);
      return;
    }

    if (typeof IntersectionObserver !== 'function') {
      setAnchorVisible(atBottom);
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        setAnchorVisible(entries.some(entry => entry.isIntersecting));
      },
      usesWindowScroll || !container ? undefined : { root: container }
    );

    observer.observe(anchor);

    return () => observer.disconnect();
  }, [anchorRef, containerRef, hasScrollableContent, atBottom, usesWindowScroll]);

  const showScrollButton = hasScrollableContent && !anchorVisible;

  return (
    <div className="relative flex-1">
      <div
        {...containerProps}
        className="h-full overflow-y-auto overscroll-contain rounded-lg bg-card"
        aria-label="Chat transcript"
        role="log"
        aria-live="polite"
      >
        <ChatMessageList
          threadId={threadId}
          messages={messages}
          isStreaming={isStreaming}
          bottomAnchorProps={bottomAnchorProps}
        />
      </div>
      {showScrollButton ? (
        <Button
          type="button"
          size="icon"
          variant="secondary"
          className="fixed right-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] z-40 shadow-md sm:bottom-[calc(6.5rem+env(safe-area-inset-bottom,0px))]"
          aria-label="Scroll to latest messages"
          onClick={scrollToBottom}
          data-testid="scroll-down"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

type ShadcnChatInnerProps = ShadcnChatProps & {
  store: ChatStore;
};

function ShadcnChatInner({
  threadId,
  apiBase,
  model,
  systemPrompt,
  tools,
  toolHandlers,
  handleToolsLocally,
  store,
}: ShadcnChatInnerProps) {
  const id = threadId ?? 'shadcn-thread';
  useThread(id);

  const resolvedTools = React.useMemo(() => tools ?? DEFAULT_TOOLS, [tools]);
  const resolvedToolHandlers = React.useMemo(() => toolHandlers ?? defaultToolHandlers, [toolHandlers]);
  const resolvedHandleLocally = handleToolsLocally ?? true;

  const [isRunning, setIsRunning] = React.useState(false);

  const handleSend = React.useCallback(
    async (assistantMessageId: Id) => {
      if (!assistantMessageId) return;
      setIsRunning(true);
      try {
        const trimmedModel = model?.trim();
        const resolvedModel = trimmedModel && trimmedModel.length > 0 ? trimmedModel : DEFAULT_MODEL;
        const trimmedSystem = systemPrompt?.trim();
        const resolvedSystem =
          systemPrompt === undefined ? DEFAULT_SYSTEM : trimmedSystem && trimmedSystem.length > 0 ? trimmedSystem : undefined;

        await runOpenAIChatWithTools({
          store,
          threadId: id,
          model: resolvedModel,
          system: resolvedSystem,
          baseURL: apiBase,
          chatCompletionsURL: apiBase,
          assistantMessageId,
          tools: resolvedTools,
          toolHandlers: resolvedToolHandlers,
          handleToolsLocally: resolvedHandleLocally,
          maxToolRounds: 4,
        });
      } finally {
        setIsRunning(false);
      }
    },
    [apiBase, id, model, resolvedHandleLocally, resolvedToolHandlers, resolvedTools, store, systemPrompt]
  );

  return (
    <div className="relative flex min-h-[420px] flex-col gap-6">
      <Conversation threadId={id} bottomThreshold={56}>
        {context => (
          <ConversationViewport
            {...context}
            threadId={id}
            isStreaming={isRunning}
          />
        )}
      </Conversation>
      <div className="sticky bottom-0 z-10 w-full">
        <ChatComposer
          threadId={id}
          disabled={isRunning}
          onSend={handleSend}
          className="border-border/80 bg-card/80 backdrop-blur supports-[backdrop-filter]:bg-card/60"
        />
      </div>
    </div>
  );
}

export function ShadcnChat(props: ShadcnChatProps) {
  const store = React.useMemo(() => createChatStore({ microBatchMs: 0 }), []);

  return (
    <ChatProvider store={store}>
      <div className="flex flex-col gap-6">
        <ShadcnChatInner {...props} store={store} />
      </div>
    </ChatProvider>
  );
}
